import React, { useEffect, useMemo, useState } from 'react';

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const Money = ({ value }) => {
  const n = safeNum(value);
  return <span>${n.toFixed(2)}</span>;
};

export default function AICostCalculator() {
  // --------- User inputs ----------
  const [period, setPeriod] = useState('month'); // day | month | year
  const [pagesPerPeriod, setPagesPerPeriod] = useState(1000);

  // Model selection: fills standard $/1M input/output token pricing presets.
  const openAiModelPresets = useMemo(
    () => [
      { id: 'custom', label: 'Custom (manual rates)' },
      // OpenAI standard pricing (input/output $ per 1M tokens)
      { id: 'gpt-4o-mini', label: 'OpenAI: gpt-4o-mini', inputPer1M: 0.15, outputPer1M: 0.6 },
      { id: 'gpt-4o', label: 'OpenAI: gpt-4o', inputPer1M: 2.5, outputPer1M: 10 },
      { id: 'gpt-4.1-mini', label: 'OpenAI: gpt-4.1-mini', inputPer1M: 0.4, outputPer1M: 1.6 },
      { id: 'gpt-4.1', label: 'OpenAI: gpt-4.1', inputPer1M: 2.0, outputPer1M: 8 },
      // Claude standard pricing (input/output $ per 1M tokens)
      { id: 'claude-haiku-4.5', label: 'Claude: Haiku 4.5', inputPer1M: 1.0, outputPer1M: 5.0 },
      { id: 'claude-sonnet-4.6', label: 'Claude: Sonnet 4.6', inputPer1M: 3.0, outputPer1M: 15.0 },
      { id: 'claude-opus-4.6', label: 'Claude: Opus 4.6', inputPer1M: 5.0, outputPer1M: 25.0 },
    ],
    []
  );
  const [openAiModel, setOpenAiModel] = useState('custom');

  // How many pages make up one "document" for OpenAI classification/extraction.
  // (Your Whisperer/Textract assumptions below are directly page-based.)
  const [avgPagesPerDocument, setAvgPagesPerDocument] = useState(1);

  // OpenAI token assumptions
  const [classificationInputTokens, setClassificationInputTokens] = useState(6000);
  const [classificationOutputTokens, setClassificationOutputTokens] = useState(500);

  const [extractionsPerDocument, setExtractionsPerDocument] = useState(30); // 20-50 range
  const [extractionInputTokensPerCall, setExtractionInputTokensPerCall] = useState(5000); // 200-10000 range
  const [extractionOutputTokensPerCall, setExtractionOutputTokensPerCall] = useState(600); // 200-1000 range

  // OpenAI token pricing (you fill these in)
  const [openAiInputPricePer1M, setOpenAiInputPricePer1M] = useState(0);
  const [openAiOutputPricePer1M, setOpenAiOutputPricePer1M] = useState(0);

  // Auto-fill token prices when a known model is selected.
  useEffect(() => {
    const preset = openAiModelPresets.find((p) => p.id === openAiModel);
    if (!preset || preset.id === 'custom') return;
    const nextInput = typeof preset.inputPer1M === 'number' ? preset.inputPer1M : 0;
    const nextOutput = typeof preset.outputPer1M === 'number' ? preset.outputPer1M : 0;
    setOpenAiInputPricePer1M(nextInput);
    setOpenAiOutputPricePer1M(nextOutput);
  }, [openAiModel, openAiModelPresets]);

  // Whisperer pricing (page-based)
  const [whispererLowPricePer1KPages, setWhispererLowPricePer1KPages] = useState(1);
  const [whispererHighPricePer1KPages, setWhispererHighPricePer1KPages] = useState(15);
  const [highQualityTablesEveryNPages, setHighQualityTablesEveryNPages] = useState(50); // 1 in 50 pages

  // AWS Textract pricing (page-based)
  const [textractPricingTier, setTextractPricingTier] = useState('regular'); // regular | discounted
  const [textractSignaturePagesFraction, setTextractSignaturePagesFraction] = useState(0); // unknown in your prompt
  const [textractTablesEveryNPages, setTextractTablesEveryNPages] = useState(50); // 1 in 50 pages

  // --------- Derived values ----------
  const computation = useMemo(() => {
    const pages = Math.max(0, safeNum(pagesPerPeriod));
    const pagesPerDoc = Math.max(0.000001, safeNum(avgPagesPerDocument)); // avoid divide-by-zero

    const docs = pages / pagesPerDoc;

    // OpenAI tokens
    const classificationInputTokensTotal = docs * Math.max(0, safeNum(classificationInputTokens));
    const classificationOutputTokensTotal = docs * Math.max(0, safeNum(classificationOutputTokens));

    const extractionCallsTotal = docs * Math.max(0, safeNum(extractionsPerDocument));
    const extractionInputTokensTotal =
      extractionCallsTotal * Math.max(0, safeNum(extractionInputTokensPerCall));
    const extractionOutputTokensTotal =
      extractionCallsTotal * Math.max(0, safeNum(extractionOutputTokensPerCall));

    const openAiInputPrice = Math.max(0, safeNum(openAiInputPricePer1M));
    const openAiOutputPrice = Math.max(0, safeNum(openAiOutputPricePer1M));

    const openAiClassificationCost =
      (classificationInputTokensTotal / 1_000_000) * openAiInputPrice +
      (classificationOutputTokensTotal / 1_000_000) * openAiOutputPrice;

    const openAiExtractionCost =
      (extractionInputTokensTotal / 1_000_000) * openAiInputPrice +
      (extractionOutputTokensTotal / 1_000_000) * openAiOutputPrice;

    const openAiTotal = openAiClassificationCost + openAiExtractionCost;

    // Whisperer pages split
    const N = Math.max(1, safeNum(highQualityTablesEveryNPages));
    const highQualityPages = pages / N;
    const lowQualityPages = pages - highQualityPages;

    const whispererLowPrice = Math.max(0, safeNum(whispererLowPricePer1KPages));
    const whispererHighPrice = Math.max(0, safeNum(whispererHighPricePer1KPages));
    const whispererCost =
      (lowQualityPages / 1000) * whispererLowPrice + (highQualityPages / 1000) * whispererHighPrice;

    // Textract
    const textractTier =
      textractPricingTier === 'discounted'
        ? {
            signaturePer1K: 1.40,
            tablesPer1K: 15.00,
          }
        : {
            signaturePer1K: 3.50,
            tablesPer1K: 20.00,
          };

    const signatureFraction = Math.min(1, Math.max(0, safeNum(textractSignaturePagesFraction)));
    const signaturePages = pages * signatureFraction;

    const texN = Math.max(1, safeNum(textractTablesEveryNPages));
    const tablesPages = pages / texN;

    const textractCost = (signaturePages / 1000) * textractTier.signaturePer1K + (tablesPages / 1000) * textractTier.tablesPer1K;

    const safeDocs = docs > 0 ? docs : 1;
    const safePages = pages > 0 ? pages : 1;

    const totalCost = openAiTotal + whispererCost + textractCost;
    const costPerDocument = totalCost / safeDocs;
    const costPerPage = totalCost / safePages;

    const llmCostPerDocument = openAiTotal / safeDocs;
    const llmCostPerPage = openAiTotal / safePages;

    const whispererCostPerPage = whispererCost / safePages;
    const textractCostPerPage = textractCost / safePages;

    return {
      period,
      pages,
      docs,
      classificationInputTokensTotal,
      classificationOutputTokensTotal,
      extractionCallsTotal,
      extractionInputTokensTotal,
      extractionOutputTokensTotal,
      openAiClassificationCost,
      openAiExtractionCost,
      openAiTotal,
      whispererLowPages: Math.max(0, lowQualityPages),
      whispererHighPages: Math.max(0, highQualityPages),
      whispererCost,
      textractCost,
      totalCost,
      costPerDocument,
      costPerPage,
      llmCostPerDocument,
      llmCostPerPage,
      whispererCostPerPage,
      textractCostPerPage,
    };
  }, [
    period,
    pagesPerPeriod,
    avgPagesPerDocument,
    classificationInputTokens,
    classificationOutputTokens,
    extractionsPerDocument,
    extractionInputTokensPerCall,
    extractionOutputTokensPerCall,
    openAiInputPricePer1M,
    openAiOutputPricePer1M,
    whispererLowPricePer1KPages,
    whispererHighPricePer1KPages,
    highQualityTablesEveryNPages,
    textractPricingTier,
    textractSignaturePagesFraction,
    textractTablesEveryNPages,
  ]);

  const openAiPricingEnabled = safeNum(openAiInputPricePer1M) > 0 || safeNum(openAiOutputPricePer1M) > 0;

  return (
    <div className="status-report">
      <div className="report-header">
        <h1 className="report-title">
          <span style={{ display: 'inline-block', transform: 'translateY(-1px)' }}>💸</span>
          AI Cost Calculator
        </h1>
        <div className="report-meta">
          <span className="meta-item">
            Period: <strong style={{ marginLeft: 6 }}>{period.toUpperCase()}</strong>
          </span>
        </div>
      </div>

      <div className="report-grid">
        <div className="report-section">
          <h2 className="section-title">Inputs</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Pages per period</label>
              <input
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                type="number"
                min={50}
                step={1}
                value={pagesPerPeriod}
                onChange={(e) => setPagesPerPeriod(safeNum(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Period</label>
              <select
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Avg pages per document</label>
              <input
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                type="number"
                min={1}
                step={1}
                value={avgPagesPerDocument}
                onChange={(e) => setAvgPagesPerDocument(safeNum(e.target.value))}
              />
              <div className="text-xs text-slate-500">
                Used to convert page volume into LLM “documents”.
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Extractions per document</label>
              <input
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                type="number"
                min={0}
                value={extractionsPerDocument}
                onChange={(e) => setExtractionsPerDocument(safeNum(e.target.value))}
              />
              <div className="text-xs text-slate-500">Prompt range: 20-50</div>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">LLM (tokens -> $)</h3>
            {!openAiPricingEnabled && (
              <div className="mb-3 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
                Token pricing is set to $0. Select a known model or set your $/1M input/output token rates to include LLM costs.
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-semibold text-slate-700">LLM model used</label>
              <select
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                value={openAiModel}
                onChange={(e) => setOpenAiModel(e.target.value)}
              >
                {openAiModelPresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <div className="text-xs text-slate-500 mt-1">
                Selecting a known model auto-fills standard token pricing ($/1M input/output).
                If you use cached/batch/premium long-context pricing, switch to `Custom (manual rates)` and edit the numbers.
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Classification input tokens (per doc)</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  value={classificationInputTokens}
                  onChange={(e) => setClassificationInputTokens(safeNum(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Classification output tokens (per doc)</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  value={classificationOutputTokens}
                  onChange={(e) => setClassificationOutputTokens(safeNum(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Extraction input tokens (per call)</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  value={extractionInputTokensPerCall}
                  onChange={(e) => setExtractionInputTokensPerCall(safeNum(e.target.value))}
                />
                <div className="text-xs text-slate-500">Prompt range: 200-10000</div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Extraction output tokens (per call)</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  value={extractionOutputTokensPerCall}
                  onChange={(e) => setExtractionOutputTokensPerCall(safeNum(e.target.value))}
                />
                <div className="text-xs text-slate-500">Prompt range: 200-1000</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">LLM input price ($ / 1M tokens)</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  step={0.0001}
                  value={openAiInputPricePer1M}
                  onChange={(e) => setOpenAiInputPricePer1M(safeNum(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">LLM output price ($ / 1M tokens)</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  step={0.0001}
                  value={openAiOutputPricePer1M}
                  onChange={(e) => setOpenAiOutputPricePer1M(safeNum(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Whisperer</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Low quality price ($ / 1K pages)</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  step={0.01}
                  value={whispererLowPricePer1KPages}
                  onChange={(e) => setWhispererLowPricePer1KPages(safeNum(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">High quality price ($ / 1K pages)</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  step={0.01}
                  value={whispererHighPricePer1KPages}
                  onChange={(e) => setWhispererHighPricePer1KPages(safeNum(e.target.value))}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700">High quality every N pages</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={1}
                  step={1}
                  value={highQualityTablesEveryNPages}
                  onChange={(e) => setHighQualityTablesEveryNPages(safeNum(e.target.value))}
                />
                <div className="text-xs text-slate-500">Prompt assumption: 1 in 50 pages</div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">AWS Textract</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Pricing tier</label>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  value={textractPricingTier}
                  onChange={(e) => setTextractPricingTier(e.target.value)}
                >
                  <option value="regular">Regular</option>
                  <option value="discounted">Discounted</option>
                </select>
                <div className="text-xs text-slate-500">Regular: Signatures $3.50/1K, Tables+Queries $20/1K</div>
                <div className="text-xs text-slate-500">Discounted: Signatures $1.40/1K, Tables+Queries $15/1K</div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Signatures pages fraction</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={textractSignaturePagesFraction}
                  onChange={(e) => setTextractSignaturePagesFraction(safeNum(e.target.value))}
                />
                <div className="text-xs text-slate-500">Not specified in prompt. Set e.g. 0.2 if 20% of pages have signatures.</div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700">Tables+Queries every N pages</label>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
                  type="number"
                  min={1}
                  step={1}
                  value={textractTablesEveryNPages}
                  onChange={(e) => setTextractTablesEveryNPages(safeNum(e.target.value))}
                />
                <div className="text-xs text-slate-500">Prompt assumption: 1 in 50 pages</div>
              </div>
            </div>
          </div>
        </div>

        <div className="report-section">
          <h2 className="section-title">Estimated Costs</h2>

          <div className="metrics-table">
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Formula basis</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>LLM Classification</td>
                  <td>docs * (input/output tokens) * $/1M tokens</td>
                  <td>
                    <Money value={computation.openAiClassificationCost} />
                  </td>
                </tr>
                <tr>
                  <td>LLM Extraction</td>
                  <td>docs * extractions * (tokens) * $/1M tokens</td>
                  <td>
                    <Money value={computation.openAiExtractionCost} />
                  </td>
                </tr>
                <tr>
                  <td>Whisperer (LLM)</td>
                  <td>pages split into low/high quality</td>
                  <td>
                    <Money value={computation.whispererCost} />
                  </td>
                </tr>
                <tr>
                  <td>AWS Textract</td>
                  <td>signatures + tables/pages</td>
                  <td>
                    <Money value={computation.textractCost} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-wrap gap-4">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-sm font-semibold text-slate-600">Total (all categories)</div>
              <div className="text-2xl font-extrabold text-indigo-600 mt-1">
                <Money value={computation.totalCost} />
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-sm font-semibold text-slate-600">Docs (derived)</div>
              <div className="text-2xl font-extrabold text-emerald-600 mt-1">
                {computation.docs.toFixed(0)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-sm font-semibold text-slate-600">Extraction calls</div>
              <div className="text-2xl font-extrabold text-amber-600 mt-1">
                {computation.extractionCallsTotal.toFixed(0)}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-md border border-slate-200 bg-white px-4 py-3">
            <div className="font-semibold mb-2">Unit Economics (derived)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-sm text-slate-700">
                <div>
                  <span className="font-semibold">LLM cost / document:</span> <Money value={computation.llmCostPerDocument} />
                </div>
                <div className="mt-1">
                  <span className="font-semibold">LLM cost / page:</span> <Money value={computation.llmCostPerPage} />
                </div>
              </div>
              <div className="text-sm text-slate-700">
                <div>
                  <span className="font-semibold">Whisperer cost / page:</span> <Money value={computation.whispererCostPerPage} />
                </div>
                <div className="mt-1">
                  <span className="font-semibold">Textract cost / page:</span> <Money value={computation.textractCostPerPage} />
                </div>
              </div>
              <div className="text-sm text-slate-700 md:col-span-2">
                <div>
                  <span className="font-semibold">Total cost / document:</span> <Money value={computation.costPerDocument} />
                </div>
                <div className="mt-1">
                  <span className="font-semibold">Total cost / page:</span> <Money value={computation.costPerPage} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div className="font-semibold mb-1">Breakdown (derived assumptions)</div>
            <div>Pages: {computation.pages.toFixed(0)}.</div>
            <div>LLM input tokens total: {(computation.classificationInputTokensTotal + computation.extractionInputTokensTotal).toFixed(0)}.</div>
            <div>LLM output tokens total: {(computation.classificationOutputTokensTotal + computation.extractionOutputTokensTotal).toFixed(0)}.</div>
            <div>Whisperer high-quality pages: {computation.whispererHighPages.toFixed(0)} (every N={highQualityTablesEveryNPages}).</div>
            <div>AWS Textract tables+queries pages: {(computation.pages / Math.max(1, safeNum(textractTablesEveryNPages))).toFixed(0)} (every N={textractTablesEveryNPages}).</div>
          </div>
        </div>
      </div>
    </div>
  );
}

